"use client";

import { sendGTMEvent } from "@next/third-parties/google";
import React, { useContext, useEffect, useEffectEvent, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select2Option from "@/app/(main)/_public_components/select2-option";
import { postPublicContact } from "@/app/api/dynabuy-back/api-contact";
import Button from "@/components/button";
import ClientFlash from "@/components/global/client-flash";
import ComponentLoading from "@/components/global/component-loading";
import HocTextInput from "@/components/global/inputs/hoc-text-input";
import TextInput from "@/components/global/inputs/text-input";
import Icon from "@/components/icon";
import { callbackContext } from "@/context/callback-context-provider";
import { useContactForm } from "@/hooks/contact/ use-contact-form";
import useCitron from "@/hooks/use-citron";
import useSubmit from "@/hooks/use-submit";
import { ContactTopicRead, GTMEventData, PublicContactWrite } from "@/TS/Interfaces/global_interfaces";
import { handleErrors } from "@/utils/formFunc/form-func";
type BlockFormValues = Omit<PublicContactWrite, "phone"> & {
  phone: string;
};
export function BlockForm() {
  const {
    register,
    reset,
    handleSubmit,
    control,
    getValues
  } = useForm<BlockFormValues>();
  const {
    isAnalyticsAccepted
  } = useCitron();
  const {
    topicsQuery,
    departmentsQuery
  } = useContactForm();
  const {
    errors,
    loading,
    onSubmit
  } = useSubmit(postPublicContact, {
    callbackMessage: {
      type: "success2",
      message: "Votre demande a été envoyée avec succès !",
      position: "/#contact"
    },
    callback: () => {
      const formValues = getValues();
      const selectedTopicId = formValues.topic;
      const selectedTopic = listOptionsNature.find(option => option.value === selectedTopicId);
      if (isAnalyticsAccepted) {
        const eventData: GTMEventData = {
          event: "Contact",
          eventCategory: "Form",
          eventAction: "Submit",
          eventLabel: "Footer Contact Form",
          pageLocation: window.location.href,
          selectedTopicLabel: selectedTopic?.label
        };
        sendGTMEvent(eventData);
      }
      setSelect2Version(prev => prev + 1);
      reset();
    },
    errorFlashPath: "/#contact"
  });
  const contactRef = useRef(null);
  const EnhancedTextInput = HocTextInput(TextInput, loading, register);
  const transformSubmit = (data: BlockFormValues): PublicContactWrite => ({
    ...data,
    phone: {
      countryCode: "+33",
      number: data.phone
    }
  });
  const [select2Version, setSelect2Version] = useState(1);
  const {
    callback,
    setCallback,
    id
  } = useContext(callbackContext)!;
  const runContactCallback = useEffectEvent((): boolean => {
    if (!callback || !contactRef.current || id !== "Contact") return false;
    callback(contactRef);
    setCallback(null);
    return true;
  });
  const contactElement = contactRef.current;
  useEffect(() => {
    if (!runContactCallback()) return;
    return () => {
      setCallback(null);
    };
  }, [callback, contactElement, setCallback]);
  if (topicsQuery.isError || departmentsQuery.isError) {
    return <div className="text-red-500 text-center">
        {topicsQuery.isError && departmentsQuery.isError ? "Impossible de charger les données du formulaire" : topicsQuery.isError ? "Impossible de charger les sujets de contact" : "Impossible de charger la liste des départements"}
      </div>;
  }
  if (topicsQuery.isLoading || departmentsQuery.isLoading) return <ComponentLoading />;
  const listOptionsDepartement = departmentsQuery.data?.map(department => ({
    value: department["@id"],
    label: department.name
  })) ?? [];
  const listOptionsNature = topicsQuery.data?.map((topic: ContactTopicRead) => ({
    value: `/helpdesk/topics/public/${topic["id"]}`,
    label: topic.label
  })) ?? [];
  const placeholder = "Sélectionner une réponse";
  return <>
      <div className={"bg-primary py-8 text-white"} ref={contactRef}>
        <div className="container">
          <h2 className="header" style={{
          lineHeight: "30px"
        }}>
            Vous souhaitez obtenir des informations complémentaires sur les
            Clubs d’Affaires ?
          </h2>
          <div className="flex flex-col md:flex-row flex-wrap">
            <div className="w-full md:w-6/12 mb-4">
              <p className={"mb-4"}>
                <b> Contactez Dynabuy, </b> du lundi au vendredi de 8h45 à 12h30
                et de 13h50 à 17h30 :
              </p>
              <a href="tel:0251241112" className={"flex items-center w-fit mb-4"}>
                <Icon size={45} icon={"phone"} className={"bg-black rounded-full mr-3"} />
                <p>
                  <b> Téléphone : </b> <br /> 02 51 25 11 12
                </p>
              </a>
              <a href="mailto:clubsdaffaires@dynabuy.fr" className={"flex items-center w-fit"}>
                <Icon size={45} icon={"mail"} className={"bg-black rounded-full mr-3"} />
                <p>
                  <b> E-mail : </b> <br /> clubsdaffaires@dynabuy.fr
                </p>
              </a>
            </div>
            <div className="w-full md:w-6/12">
              <p className={"mb-4"}>
                <b> Ou laissez-nous vos coordonnées, </b> notre équipe vous
                rappelle dès que possible.
              </p>
              <div className={"bg-white rounded-2xl"}>
                <ClientFlash path={"/#contact"} />
              </div>
              <form className={"flex flex-wrap"} onSubmit={handleSubmit(data_0 => onSubmit(transformSubmit(data_0)))}>
                <EnhancedTextInput name={"firstName"} error={handleErrors(errors, "firstName")} errorsClassName={"bg-white rounded p-2"} label={"Prénom *"} needed={false} placeholder={"Saisir un prénom"} className={"bg-white"} containerClassName={"w-full lg:w-6/12 pr-0 lg:pr-4 mb-2"} requireClassname={"!text-white"} inputClassname={"text-dark"} />
                <EnhancedTextInput name={"lastName"} error={handleErrors(errors, "lastName")} errorsClassName={"bg-white rounded p-2"} label={"Nom *"} needed={false} placeholder={"Saisir un nom"} className={"bg-white"} containerClassName={"w-full lg:w-6/12 pl-0 lg:pl-4 mb-2"} requireClassname={"!text-white"} inputClassname={"text-dark"} />
                <EnhancedTextInput name={"jobTitle"} error={handleErrors(errors, "jobTitle")} errorsClassName={"bg-white rounded p-2"} label={"Fonction *"} needed={false} placeholder={"Saisir une réponse"} className={"bg-white"} containerClassName={"w-full lg:w-6/12 pr-0 lg:pr-4 mb-2"} requireClassname={"!text-white"} inputClassname={"text-dark"} />
                <EnhancedTextInput name={"companyName"} error={handleErrors(errors, "companyName")} errorsClassName={"bg-white rounded p-2"} label={"Société / Raison sociale *"} needed={false} placeholder={"Saisir un nom de société"} className={"bg-white"} containerClassName={"w-full lg:w-6/12 pl-0 lg:pl-4 mb-2"} requireClassname={"!text-white"} inputClassname={"text-dark"} />
                <EnhancedTextInput name={"phone"} error={handleErrors(errors, "phone")} errorsClassName={"bg-white rounded p-2"} label={"Numéro de téléphone *"} needed={false} placeholder={"Saisir un numéro de téléphone"} className={"bg-white"} containerClassName={"w-full lg:w-6/12 pr-0 lg:pr-4 mb-2"} requireClassname={"!text-white"} inputClassname={"text-dark"} />
                <EnhancedTextInput name={"email"} error={handleErrors(errors, "email")} errorsClassName={"bg-white rounded p-2"} label={"E-mail *"} needed={false} placeholder={"Saisir un e-mail"} className={"bg-white"} containerClassName={"w-full lg:w-6/12 pl-0 lg:pl-4 mb-2"} requireClassname={"!text-white"} inputClassname={"text-dark"} />
                <div className={"w-full lg:w-6/12 pr-0 lg:pr-4 mb-2"}>
                  <Controller control={control} render={({
                  field
                }) => <Select2Option {...field} error={handleErrors(errors, "department")} errorClassName={"bg-white rounded p-2"} isClearable={false} placeholder={placeholder} options={listOptionsDepartement} label={"Département souhaité *"} versionKey={select2Version} onChange={(selectedOption: {
                  value: string;
                  label: string;
                }) => field.onChange(selectedOption.value)} />} name={"department"}></Controller>
                </div>
                <div className={"w-full lg:w-6/12 pl-0 lg:pl-4 mb-2"}>
                  <Controller control={control} render={({
                  field: field_0
                }) => <Select2Option error={handleErrors(errors, "nature")} errorClassName={"bg-white rounded p-2"} placeholder={placeholder} options={listOptionsNature} isClearable={false} label={"Nature de la demande *"} versionKey={select2Version} onChange={(selectedOption_0: {
                  value: string;
                  label: string;
                }) => field_0.onChange(selectedOption_0.value)} />} name={"topic"}></Controller>
                </div>
                <p className={"w-full mb-4"}>* Champs obligatoires</p>
                <Button type={"submit"} className={"bg-dark w-4/12 mx-auto mb-8"} disabled={loading}>
                  Envoyer
                </Button>
              </form>
              <p style={{
              lineHeight: "0.9rem",
              fontSize: "0.65rem",
              marginBottom: "1rem"
            }}>
                Lorsque vous nous contactez, les informations que vous
                communiquez à DYNABUY ne sont utilisées qu’aux fins de gestion
                de votre demande, sur la base de l’intérêt légitime de DYNABUY à
                répondre aux demandes de contact qui nous sont adressées.
                DYNABUY ne conserve les données à caractère personnel traitées
                que pour la durée nécessaire aux opérations pour lesquelles
                elles ont été collectées ainsi que dans le respect de la
                réglementation en vigueur. Pour tout complément d’information,
                vous pouvez consulter notre page politique de confidentialité.
                Les droits d’accès, de rectification, d’opposition,
                d’effacement, à la portabilité, à la limitation du traitement
                peuvent être exercés par courriel à l’adresse dpo@dynabuy.fr
                DYNABUY apporte une grande attention aux données à caractère
                personnel qui lui sont confiées. Si vous considérez que le
                traitement des données vous concernant porte atteinte à vos
                droits, vous disposez de la faculté d’introduire une réclamation
                auprès de la CNIL.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}